import hubspotControls from '@/utilities/hubspotControls';
import { CircularProgress, useMediaQuery } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { css } from '@emotion/react';
import useQuery from '@/utilities/useQuery';
import { postUnauth } from '@/utilities/onwardClient';
import { GENERATE_CUSTOMER_TOKEN, VERIFY_TOKEN } from '@/constants/apiRoutes';
import { captureException } from '@sentry/react';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { UserContext } from '../App';

const verifyToken = (role, token) => {
    return postUnauth(`${VERIFY_TOKEN}/${role}`, { token }).then((result) => result.data);
};

const genToken = () => {
    return postUnauth(GENERATE_CUSTOMER_TOKEN, {}).then((result) => result.data);
};

const withTokenAuth =
    ({ role, reqClaims = [], redirectLocation }) =>
    (Component) =>
    (props) => {
        const { search } = useLocation();
        const navigate = useNavigate();
        const [authenticated, setAuthenticated] = useState(false);
        const [claims, setClaims] = useState({});
        const [token, setToken] = useState(null);
        const mobile = useMediaQuery('(max-width: 600px)');

        const { firebaseUser } = useContext(UserContext);

        const handleNavigation = () => {
            if (redirectLocation) {
                navigate(redirectLocation);
            } else {
                navigate('/signin');
            }
        };

        const handleError = (error) => {
            captureException(error);
            console.error(error);
            handleNavigation();
        };

        const handleToken = ({ verified, token }) => {
            if (token) {
                setToken(token);
                setClaims(verified);
                if (!localStorage?.getItem('onward.already-signed-in')) {
                    console.debug('signing in with custom token');
                    signInWithCustomToken(getAuth(), token);
                }
            } else {
                handleNavigation();
            }
        };

        const [verify] = useQuery(verifyToken, {
            onError: handleError,
            onComplete: handleToken,
        });

        const [generate] = useQuery(genToken, {
            onError: handleError,
            onComplete: handleToken,
        });

        useEffect(() => {
            hubspotControls.hide();
            return () => hubspotControls.show();
        }, []);

        useEffect(() => {
            const params = new URLSearchParams(search);
            const jwt = params.get('jwt') || params.get('token');

            if (jwt) {
                verify(role, jwt);
            } else {
                generate();
            }
        }, [search]);

        useEffect(() => {
            if (firebaseUser && token) {
                firebaseUser.getIdTokenResult().then(({ claims: authClaims }) => {
                    const hasuraClaims = authClaims?.['https://hasura.io/jwt/claims'];
                    const authRole = hasuraClaims?.['x-hasura-default-role'];

                    if (
                        authRole === 'user' ||
                        authRole === 'onward-admin' ||
                        (authRole === role &&
                            (reqClaims || []).every((claim) => claims?.[claim] === hasuraClaims?.[`x-hasura-${claim}`]))
                    ) {
                        setAuthenticated(true);
                    } else {
                        console.log('token doesnt contain required claims, signing out and trying again');
                        signOut(getAuth()).then(() => {
                            signInWithCustomToken(getAuth(), token);
                        });
                    }
                });
            }
        }, [firebaseUser, claims, token]);

        return authenticated ? (
            <Component {...props} claims={claims} />
        ) : (
            <div
                css={css`
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <CircularProgress size={mobile ? 100 : 200} />
            </div>
        );
    };

export default withTokenAuth;
